<template>
  <transition name="filter-job-families-modal" appear>
    <div
      v-if="featureEnabled && value"
      class="filter-job-families-modal__overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="filter-job-families-modal-title"
      @mousedown.self="handleClose"
    >
      <div class="filter-job-families-modal__card">
        <div class="filter-job-families-modal__header">
          <span id="filter-job-families-modal-title" class="filter-job-families-modal__title">
            Filter job families
          </span>
          <button
            type="button"
            class="filter-job-families-modal__close-btn"
            aria-label="Close"
            @click="handleClose"
          >
            <Icon name="popup_close" />
          </button>
        </div>

        <div class="filter-job-families-modal__filters-row">
          <div class="filter-job-families-modal__column">
            <span class="filter-job-families-modal__column-title">Status</span>
            <div class="filter-job-families-modal__options">
              <CheckboxInput
                v-for="option in statusOptions"
                :key="option.value"
                :label="option.label"
                :value="statuses[option.value]"
                @input="updateStatus(option.value, $event)"
              />
            </div>
          </div>

          <div
            class="filter-job-families-modal__column filter-job-families-modal__column--radio"
          >
            <RadioInput
              label="Archived"
              :availableItems="archivedOptions"
              :checkedItem="archived ? 'Show' : 'Do Not Show'"
              @changeradio="onArchivedChange"
            />
          </div>
        </div>

        <div class="filter-job-families-modal__footer">
          <button type="button" class="filter-job-families-modal__reset" @click="handleReset">
            Reset to defaults
          </button>
          <StandardButton
            type="magenta"
            variant="primary"
            size="small"
            label="Apply"
            @click="handleApply"
          />
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted, getCurrentInstance } from "vue";
import Icon from "@components/icons/Icon.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import CheckboxInput from "@components/Checkbox/CheckboxInput.vue";
import RadioInput from "@components/Radio/RadioInput.vue";
import { LD_FLAG_IDEA_MDP_581 } from "@components/constants/launchDarklyFlagKeys";

/**
 * ER-11652 — FilterJobFamiliesModal (Organization > Job Families filter modal).
 *
 * Follows the Filter Modal Standard (2026-08-08): a Status checkbox group
 * (Draft/Active/Retired, Draft+Active default) and an "Archived" radio
 * (Do Not Show / Show, Do Not Show default). Two groups only — the object-specific
 * "Used" group was dropped on 2026-09-16. The parent owns data fetching; this
 * component re-emits `apply`, `reset`, `input` and `close`.
 *
 * Values are stable lowercase snake_case end-to-end:
 *   - statuses:  { draft, active, retired }  (checkbox booleans)
 *   - archived:  false (default, Do Not Show) | true (Show)
 *
 * Shipped behind idea-MDP-581. Kill-switch: renders by default; set the flag to
 * false to keep the modal closed until a screen wires it.
 */
const props = defineProps({
  value: { type: Boolean, default: false },
  initialStatuses: {
    type: Object,
    default: () => ({ draft: true, active: true, retired: false }),
  },
  initialArchived: { type: Boolean, default: false },
});

const emit = defineEmits(["input", "close", "apply", "reset"]);

const DEFAULT_STATUSES = { draft: true, active: true, retired: false };
const DEFAULT_ARCHIVED = false;

const statusOptions = [
  { label: "Draft", value: "draft" },
  { label: "Active", value: "active" },
  { label: "Retired", value: "retired" },
];

const archivedOptions = [{ label: "Do Not Show" }, { label: "Show" }];

const statuses = ref({ ...DEFAULT_STATUSES, ...(props.initialStatuses || {}) });
const archived = ref(props.initialArchived);

const app = getCurrentInstance();
const { proxy } = app || {};
const featureEnabled = ref(true);

watch(
  () => props.value,
  (open) => {
    if (open) syncFromProps();
  },
  { immediate: true }
);

onMounted(() => {
  document.addEventListener("keydown", handleKeydown);
  checkFeatureFlag();
});

async function checkFeatureFlag() {
  try {
    featureEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_581, true)) !== false;
  } catch {
    featureEnabled.value = true;
  }
}

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeydown);
});

function syncFromProps() {
  statuses.value = { ...DEFAULT_STATUSES, ...(props.initialStatuses || {}) };
  archived.value = props.initialArchived;
}

function updateStatus(key, checked) {
  statuses.value = { ...statuses.value, [key]: !!checked };
}

function onArchivedChange(payload) {
  archived.value = payload?.value === "Show";
}

function handleReset() {
  statuses.value = { ...DEFAULT_STATUSES };
  archived.value = DEFAULT_ARCHIVED;
  emit("reset");
}

function handleApply() {
  emit("apply", {
    statuses: { ...statuses.value },
    archived: archived.value,
  });
  emit("input", false);
}

function handleClose() {
  emit("input", false);
  emit("close");
}

function handleKeydown(event) {
  if (event.key === "Escape" && props.value && featureEnabled.value) handleClose();
}
</script>

<style scoped>
.filter-job-families-modal-enter-active,
.filter-job-families-modal-leave-active {
  transition: opacity 180ms ease;
}

.filter-job-families-modal-enter-from,
.filter-job-families-modal-leave-to {
  opacity: 0;
}

.filter-job-families-modal-enter-active .filter-job-families-modal__card,
.filter-job-families-modal-leave-active .filter-job-families-modal__card {
  transition: transform 200ms cubic-bezier(0.22, 1, 0.36, 1), opacity 180ms ease;
}

.filter-job-families-modal-enter-from .filter-job-families-modal__card,
.filter-job-families-modal-leave-to .filter-job-families-modal__card {
  transform: translateY(8px) scale(0.98);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .filter-job-families-modal-enter-active,
  .filter-job-families-modal-leave-active,
  .filter-job-families-modal-enter-active .filter-job-families-modal__card,
  .filter-job-families-modal-leave-active .filter-job-families-modal__card {
    /* Override enter/leave transition durations when the user prefers reduced motion. */
    transition: none !important;
  }
}

.filter-job-families-modal__overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(16, 24, 40, 0.5);
  font-family: Inter, sans-serif;
}

.filter-job-families-modal__card {
  width: 100%;
  max-width: 1032px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 8px 8px -4px rgba(16, 24, 40, 0.03), 0 20px 24px -4px rgba(16, 24, 40, 0.08);
  box-sizing: border-box;
}

.filter-job-families-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.filter-job-families-modal__title {
  color: #101828;
  font-size: 18px;
  font-weight: 600;
  line-height: normal;
}

.filter-job-families-modal__close-btn {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 4px;
  margin: -4px;
  cursor: pointer;
  color: #98a2b3;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.filter-job-families-modal__close-btn:hover {
  background: rgba(152, 162, 179, 0.12);
}

.filter-job-families-modal__close-btn:focus-visible,
.filter-job-families-modal__reset:focus-visible {
  outline: 2px solid #98a2b3;
  outline-offset: 2px;
}

.filter-job-families-modal__filters-row {
  display: flex;
  gap: 32px;
  align-items: flex-start;
  width: 100%;
}

.filter-job-families-modal__column {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-job-families-modal__column-title {
  padding-left: 8px;
  color: #344054;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.filter-job-families-modal__options {
  display: flex;
  flex-direction: column;
  padding-left: 8px;
}

.filter-job-families-modal__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.filter-job-families-modal__reset {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 4px;
  margin: -4px;
  color: #475467;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: normal;
  cursor: pointer;
  border-radius: 6px;
}

.filter-job-families-modal__reset:hover {
  color: #344054;
  background: rgba(152, 162, 179, 0.08);
}
</style>
